import { FC } from 'react';
import { IonButton, IonModal } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { AddCareRecipientLauncher } from './AddCareRecipientLauncher';
import { KeyboardIcon, MicrophoneIcon } from '../theme/Icons';

const useStyles = createUseStyles({
    modal: {
        '--height': 'auto',
        '--border-radius': '16px 16px 0 0'
    },
    content: {
        padding: '28px 24px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        backgroundColor: '#fff'
    },
    title: {
        fontSize: 20,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 4,
        textAlign: 'center',
        letterSpacing: 'normal'
    },
    subtitle: {
        fontSize: 14,
        color: '#687f87',
        lineHeight: 1.5,
        marginBottom: 12,
        textAlign: 'center',
        letterSpacing: 'normal'
    },
    button: {
        '--padding-top': '20px',
        '--padding-bottom': '20px',
        '--padding-start': '20px',
        '--padding-end': '20px',
        height: 'auto',
        margin: 0
    },
    buttonInner: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        textAlign: 'left'
    },
    buttonIcon: {
        flexShrink: 0
    },
    buttonText: {
        flex: 1
    },
    buttonLabel: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 2,
        letterSpacing: 'normal'
    },
    buttonDescription: {
        fontSize: 13,
        fontWeight: 400,
        opacity: 0.85,
        textTransform: 'none',
        whiteSpace: 'normal',
        lineHeight: 1.4,
        letterSpacing: 'normal'
    }
});

export const AddCareRecipientChoiceSheet: FC = () => {
    const classes = useStyles();
    const launcher = useInstance(AddCareRecipientLauncher);
    useSubscription(() => launcher);

    return (
        <IonModal
            isOpen={launcher.state.isChoiceOpen}
            initialBreakpoint={1}
            breakpoints={[0, 1]}
            onDidDismiss={launcher.cancel}
            className={classes.modal}
            data-id="add-care-recipient-choice-sheet"
        >
            <div className={classes.content}>
                <div className={classes.title}>How would you like to set up this care profile?</div>
                <div className={classes.subtitle}>
                    Record a quick voice intro and Bea will create it automatically, or enter the details yourself.
                </div>

                <IonButton
                    expand="block"
                    color="primary"
                    className={classes.button}
                    onClick={launcher.chooseVoice}
                    data-id="add-care-recipient-voice-btn"
                >
                    <div className={classes.buttonInner}>
                        <div className={classes.buttonIcon}>
                            <MicrophoneIcon color="light" customSize={22} />
                        </div>
                        <div className={classes.buttonText}>
                            <div className={classes.buttonLabel}>Use Voice</div>
                            <div className={classes.buttonDescription}>
                                Tell Bea and she&apos;ll fill in the basics
                            </div>
                        </div>
                    </div>
                </IonButton>

                <IonButton
                    expand="block"
                    fill="outline"
                    color="primary"
                    className={classes.button}
                    onClick={launcher.chooseType}
                    data-id="add-care-recipient-type-btn"
                >
                    <div className={classes.buttonInner}>
                        <div className={classes.buttonIcon}>
                            <KeyboardIcon color="primary" customSize={22} />
                        </div>
                        <div className={classes.buttonText}>
                            <div className={classes.buttonLabel}>Type It In</div>
                            <div className={classes.buttonDescription}>
                                Walk through a short questionnaire
                            </div>
                        </div>
                    </div>
                </IonButton>
            </div>
        </IonModal>
    );
};
